import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useApi, useMe } from '../context';
import { CALL_STATE_LABEL } from '../types';
import { useEffect as useEffect2, useState as useState2 } from 'react';
import { useCallEvents } from '../realtime';
import type { CallDetail, CallType } from '../types';

/** Handle a single call: choose the type, work through its knowledgebase, capture notes. */
export function CallDetailPage() {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const me = useMe();
  const canEdit = me.roles.some((r) => r === 'on_call_user' || r === 'manager' || r === 'admin');

  const [call, setCall] = useState<CallDetail | null>(null);
  const [types, setTypes] = useState<CallType[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<CallDetail>(`/api/calls/${id}`).then((c) => {
      setCall(c);
      setNotes(c.notes ?? '');
    }, (e: Error) => setError(e.message));
  }, [api, id]);

  useEffect(load, [load]);
  useCallEvents(useCallback((_t: string, data: unknown) => {
    if ((data as { callId?: string }).callId === id) load();
  }, [id, load]));
  useEffect(() => {
    api.get<CallType[]>('/api/call-types').then(setTypes, (e: Error) => setError(e.message));
  }, [api]);

  async function save(patch: Record<string, unknown>) {
    setSaving(true);
    try {
      await api.patch(`/api/calls/${id}`, patch);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function setKb(kbItemId: string, patch: { isDone?: boolean; response?: string }) {
    await api.put(`/api/calls/${id}/kb`, { kbItemId, ...patch }).catch((e: Error) => setError(e.message));
  }

  if (error) return <p className="error">{error}</p>;
  if (!call) return <p>Loading…</p>;

  const selectedType = types.find((t) => t.name === call.call_type);

  return (
    <>
      <h1>
        Call from {call.caller_number ?? 'unknown number'}{' '}
        <span className={`badge ${call.state}`}>{CALL_STATE_LABEL[call.state]}</span>
        {call.completed_at && <span className="badge completed">completed</span>}
      </h1>
      <dl>
        {call.caller_name && <><dt>Caller</dt><dd>{call.caller_name}</dd></>}
        <dt>Rang at</dt><dd>{new Date(call.ringing_at).toLocaleString()}</dd>
        <dt>Line called</dt>
        <dd>{call.originating_queue_name ?? '—'}{call.originating_queue_number && ` (${call.originating_queue_number})`}</dd>
        <dt>Answered in</dt><dd>{call.answered_queue_name ?? '—'}</dd>
        <dt>Answered by</dt><dd>{call.answered_by ?? '—'} {call.answered_at && `at ${new Date(call.answered_at).toLocaleTimeString()}`}</dd>
      </dl>

      <h2>Call type</h2>
      <select
        disabled={!canEdit || saving}
        value={selectedType?.id ?? ''}
        onChange={(e) => save({ callTypeId: e.target.value || null })}
      >
        <option value="">Select a type…</option>
        {types.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>

      {call.knowledgebase.length > 0 && (
        <>
          <h2>Questions &amp; actions</h2>
          <ul className="kb">
            {call.knowledgebase.map((item) => (
              <li key={item.id}>
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  defaultChecked={item.is_done ?? false}
                  onChange={(e) => setKb(item.id, { isDone: e.target.checked })}
                />
                <div>
                  <div><span className="badge">{item.kind}</span> {item.text}{item.is_required && ' *'}</div>
                  {item.kind === 'question' && (
                    <input
                      style={{ width: '100%', marginTop: '0.25rem' }}
                      disabled={!canEdit}
                      placeholder="Answer"
                      defaultValue={item.response ?? ''}
                      onBlur={(e) => setKb(item.id, { response: e.target.value })}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {call.has_voicemail && (
        <>
          <h2>Voicemail</h2>
          <VoicemailPlayer callId={call.id} />
          {call.voicemail?.transcript && (
            <>
              <p className="sub">Transcript (automatic, from Teams voicemail — check against the recording):</p>
              <blockquote style={{ whiteSpace: 'pre-wrap' }}>{call.voicemail.transcript}</blockquote>
            </>
          )}
        </>
      )}

      <h2>Notes</h2>
      <textarea disabled={!canEdit} value={notes} onChange={(e) => setNotes(e.target.value)} />
      {canEdit && (
        <p>
          <label className="sub" style={{ marginRight: '0.75rem' }}>
            <input type="checkbox" checked={call.include_in_handover} disabled={saving} onChange={(e) => save({ includeInHandover: e.target.checked })} />{' '}
            Include in handover
          </label>
          <button onClick={() => save({ notes })} disabled={saving}>Save notes</button>{' '}
          {!call.completed_at ? (
            <button className="primary" onClick={() => save({ notes, completed: true })} disabled={saving}>
              Save &amp; complete
            </button>
          ) : (
            <>
              <span className="sub">Completed {new Date(call.completed_at).toLocaleString()}{call.handled_by && ` by ${call.handled_by}`}</span>{' '}
              <button className="link" onClick={() => save({ completed: false })} disabled={saving}>reopen</button>
            </>
          )}
        </p>
      )}
    </>
  );
}


function VoicemailPlayer({ callId }: { callId: string }) {
  const api = useApi();
  const [url, setUrl] = useState2<string | null>(null);
  const [error, setError] = useState2<string | null>(null);
  useEffect2(() => {
    let revoked: string | null = null;
    // The audio endpoint needs the bearer token, so fetch as a blob and play from an object URL.
    api.blob(`/api/calls/${callId}/voicemail`).then((b) => { const u = URL.createObjectURL(b); revoked = u; setUrl(u); },
      (e: Error) => setError(e.message));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [api, callId]);
  if (error) return <p className="error">Voicemail unavailable: {error}</p>;
  if (!url) return <p>Loading voicemail…</p>;
  return <audio controls src={url} style={{ width: '100%' }} />;
}
